import "./site/error-beacon";
import { Slipstream, SlipstreamResult, RUN_MS } from "./games/slipstream/slipstream";
import { recordRun, showXpToast } from "./site/profile";
import { enqueueFailedSubmit, flushFailedSubmits, isRetryable } from "./site/offline-queue";
import "./ui/styles.css";

const PB_KEY = "aether.slipstream_pb";
const HANDLE_KEY = "aether.handle";

flushFailedSubmits("slipstream");

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startSlipstreamRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/slipstream", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishSlipstreamRun(
  runId: string,
  handle: string,
  samples: SlipstreamResult["samples"],
  nearMisses: SlipstreamResult["nearMisses"],
  hit: SlipstreamResult["hitEvent"],
) {
  const url = `/api/runs/slipstream/${runId}`;
  const body = { handle, samples, near_misses: nearMisses, hit };
  try {
    const res = await fetch(url, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (isRetryable(res.status)) enqueueFailedSubmit("slipstream", url, body);
      return null;
    }
    return await res.json();
  } catch {
    enqueueFailedSubmit("slipstream", url, body);
    return null;
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudDistance = document.getElementById("hud-distance")!;
const hudTime = document.getElementById("hud-time")!;
const hudMult = document.getElementById("hud-mult")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;

let game: Slipstream | null = null;
let activeRunId: string | null = null;
let pendingResult: SlipstreamResult | null = null;

async function startRun() {
  if (game) game.dispose();
  const runStart = await startSlipstreamRun();
  activeRunId = runStart?.run_id ?? null;
  const seed = runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  game = new Slipstream(canvas, {
    onHud: (runMs, distance, multiplier, scoreEstimate) => {
      hudDistance.textContent = Math.floor(distance).toString();
      hudTime.textContent = ((RUN_MS - runMs) / 1000).toFixed(1);
      hudMult.textContent = `${multiplier}×`;
      hudScore.textContent = scoreEstimate.toLocaleString();
    },
    onGameOver: async (result) => {
      hud.dataset.state = "over";
      msgTitle.textContent = result.hit ? "Hit" : "Run complete";
      const biomesNote = result.biomes_visited > 1 ? ` · ${result.biomes_visited} biomes` : "";
      const puNote = result.powerups_collected > 0 ? ` · ${result.powerups_collected} power-ups` : "";
      msgBody.textContent = `${result.scoreEstimate.toLocaleString()} pts · ${Math.floor(result.distance_m)}m · ${result.near_misses} near · peak ${result.peak_multiplier}×${puNote}${biomesNote}`;
      btnStart.textContent = "Try again";

      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (result.scoreEstimate > pb) localStorage.setItem(PB_KEY, String(result.scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "slipstream", score: result.scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishSlipstreamRun(activeRunId, handle, result.samples, result.nearMisses, result.hitEvent);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
      } else {
        pendingResult = result;
        submitForm.hidden = false;
      }
    },
  }, seed);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Slipstream";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingResult) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishSlipstreamRun(activeRunId, value, pendingResult.samples, pendingResult.nearMisses, pendingResult.hitEvent);
  pendingResult = null;
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
